import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface HeadScript {
  id: string;
  name: string;
  description?: string;
  script_content: string;
  is_active: boolean;
  priority: number;
  script_type: 'analytics' | 'heatmap' | 'conversion' | 'other';
  created_at: string;
}

export function useHeadScripts() {
  const queryClient = useQueryClient();

  // Buscar todos os scripts (SuperAdmin)
  const { data: scripts, isLoading } = useQuery({
    queryKey: ['head_scripts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('head_scripts')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as HeadScript[];
    },
  });

  // Buscar apenas scripts ativos (para injeção no head)
  const { data: activeScripts } = useQuery({
    queryKey: ['head_scripts', 'active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('head_scripts')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: false });

      if (error) throw error;
      return data as HeadScript[];
    },
  });

  // Criar novo script
  const createScript = useMutation({
    mutationFn: async (script: Omit<HeadScript, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('head_scripts')
        .insert([script])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['head_scripts'] });
      toast({
        title: 'Script criado',
        description: 'O código foi adicionado com sucesso!',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao criar script',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Atualizar script
  const updateScript = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<HeadScript> & { id: string }) => {
      const { data, error } = await supabase
        .from('head_scripts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['head_scripts'] });
      toast({
        title: 'Script atualizado',
        description: 'As alterações foram salvas!',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Deletar script
  const deleteScript = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('head_scripts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['head_scripts'] });
      toast({
        title: 'Script removido',
        description: 'O código foi deletado com sucesso.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao deletar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Toggle ativo/inativo
  const toggleScript = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from('head_scripts')
        .update({ is_active })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['head_scripts'] });
    },
  });

  return {
    scripts,
    activeScripts,
    isLoading,
    createScript,
    updateScript,
    deleteScript,
    toggleScript,
  };
}
